import { acquireMinimalGraphToken } from './auth'

export const getGraphToken = async () => acquireMinimalGraphToken()

export const getDelegatedAdminRelationships = async graphToken => {
  let results = []
  let next = 'https://graph.microsoft.com/beta/tenantRelationships/delegatedAdminRelationships?$filter=status%20eq%20%27active%27&$select=displayName,status,customer,accessDetails,endDateTime,autoExtendDuration,id'
  while (next) {
    const resp = await fetch(next, { headers: { Authorization: `Bearer ${graphToken}` } })
    const json = await resp.json()
    results = results.concat(json.value || [])
    next = json['@odata.nextLink']
  }
  return results
}

const buildBatch = (ids) => {
  const core = '/tenantRelationships/delegatedAdminRelationships/$id/accessAssignments'
  const requests = ids.map((id, idx) => ({ id: `${idx + 1}`, method: 'GET', url: core.replace('$id', id) }))
  const batches = []
  while (requests.length) batches.push({ requests: requests.splice(0, 20) })
  return batches
}

export const getAccessAssignments = async (graphToken, relationships) => {
  const ids = relationships.map(r => r.id)
  const batches = buildBatch(ids)
  const calls = batches.map(b => fetch('https://graph.microsoft.com/beta/$batch', {
    method: 'POST',
    headers: { Authorization: `Bearer ${graphToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(b),
  }).then(r => r.json()))
  const responses = await Promise.all(calls)
  const flat = []
  responses.forEach(res => res?.responses?.forEach(item => flat.push(item)))
  return flat
}

const buildGroupDetailsBatch = ids => {
  // Use the groupId itself as the request id to simplify response mapping
  const requests = ids.map(id => ({ id: `${id}`, method: 'GET', url: `/groups/${id}?$select=id,displayName` }))
  const batches = []
  while (requests.length) batches.push({ requests: requests.splice(0, 20) })
  return batches
}

const buildGroupMembersBatch = ids => {
  // Use transitiveMembers to account for nested group membership and tag with groupId as request id
  const requests = ids.map(id => ({ id: `${id}`, method: 'GET', url: `/groups/${id}/transitiveMembers?$select=id,displayName,userPrincipalName,mail` }))
  const batches = []
  while (requests.length) batches.push({ requests: requests.splice(0, 20) })
  return batches
}

export const getSecurityGroupsWithMembers = async (graphToken, groupIds) => {
  const unique = Array.from(new Set(groupIds.filter(Boolean)))
  if (unique.length === 0) return []

  const detailBatches = buildGroupDetailsBatch(unique)
  const memberBatches = buildGroupMembersBatch(unique)

  const detailCalls = detailBatches.map(b => fetch('https://graph.microsoft.com/beta/$batch', {
    method: 'POST',
    headers: { Authorization: `Bearer ${graphToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(b),
  }).then(r => r.json()))

  const memberCalls = memberBatches.map(b => fetch('https://graph.microsoft.com/beta/$batch', {
    method: 'POST',
    headers: { Authorization: `Bearer ${graphToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(b),
  }).then(r => r.json()))

  const [detailResponses, memberResponses] = await Promise.all([
    Promise.all(detailCalls),
    Promise.all(memberCalls),
  ])

  const detailsFlat = []
  detailResponses.forEach(res => res?.responses?.forEach(item => detailsFlat.push(item)))
  const membersFlat = []
  memberResponses.forEach(res => res?.responses?.forEach(item => membersFlat.push(item)))

  const idToDetails = new Map()
  detailsFlat.forEach(r => {
    const body = r?.body
    if (body?.id) idToDetails.set(body.id, body)
  })
  const idToMembers = new Map()
  membersFlat.forEach(r => {
    const groupIdFromRequest = r?.id
    const members = (r?.body?.value || []).map(m => ({
      id: m.id,
      displayName: m.displayName,
      userPrincipalName: m.userPrincipalName,
      mail: m.mail,
    }))
    if (groupIdFromRequest) idToMembers.set(groupIdFromRequest, members)
  })

  return unique.map(id => ({
    details: idToDetails.get(id) || { id, displayName: 'Unknown Group' },
    members: idToMembers.get(id) || [],
  }))
}


